// Convention:
// - Everything lowercase is an object
// - Everything uppercase is a string (the route)

import { keys } from '../keys';

export const baseUrl = {
  Portal: keys().NEXT_PUBLIC_PORTAL_URL,
  Marketing: keys().NEXT_PUBLIC_MARKETING_URL
} as const;

export const routes = {
  portal: {
    Api: `${baseUrl.Portal}/api`,
    auth: {
      changeEmail: {
        Expired: `${baseUrl.Portal}/auth/change-email/expired`,
        Index: `${baseUrl.Portal}/auth/change-email`,
        Invalid: `${baseUrl.Portal}/auth/change-email/invalid`,
        Request: `${baseUrl.Portal}/auth/change-email/request`
      },
      Error: `${baseUrl.Portal}/auth/error`,
      forgetPassword: {
        Index: `${baseUrl.Portal}/auth/forgot-password`,
        Success: `${baseUrl.Portal}/auth/forgot-password/success`
      },
      Index: `${baseUrl.Portal}/auth`,
      RecoveryCode: `${baseUrl.Portal}/auth/recovery-code`,
      resetPassword: {
        Expired: `${baseUrl.Portal}/auth/reset-password/expired`,
        Index: `${baseUrl.Portal}/auth/reset-password`,
        Request: `${baseUrl.Portal}/auth/reset-password/request`,
        Success: `${baseUrl.Portal}/auth/reset-password/success`
      },
      SignIn: `${baseUrl.Portal}/auth/sign-in`,
      SignUp: `${baseUrl.Portal}/auth/sign-up`,
      Totp: `${baseUrl.Portal}/auth/totp`,
      verifyEmail: {
        Expired: `${baseUrl.Portal}/auth/verify-email/expired`,
        Index: `${baseUrl.Portal}/auth/verify-email`,
        Request: `${baseUrl.Portal}/auth/verify-email/request`,
        Success: `${baseUrl.Portal}/auth/verify-email/success`
      }
    },
    Index: `${baseUrl.Portal}/`,
    invitations: {
      AlreadyAccepted: `${baseUrl.Portal}/invitations/already-accepted`,
      Index: `${baseUrl.Portal}/invitations`,
      Request: `${baseUrl.Portal}/invitations/request`,
      Revoked: `${baseUrl.Portal}/invitations/revoked`
    },
    onboarding: {
      Index: `${baseUrl.Portal}/onboarding`,
      Space: `${baseUrl.Portal}/onboarding/space`,
      User: `${baseUrl.Portal}/onboarding/user`
    },
    spaces: {
      Index: `${baseUrl.Portal}/spaces`,
      slug: {
        ChoosePlan: `${baseUrl.Portal}/spaces/[slug]/choose-plan`,
        'Ask AI': `${baseUrl.Portal}/spaces/[slug]/ask-ai`,
        Files: `${baseUrl.Portal}/spaces/[slug]/files`,
        Trash: `${baseUrl.Portal}/spaces/[slug]/trash`,
        Starred: `${baseUrl.Portal}/spaces/[slug]/starred`,
        Notes: `${baseUrl.Portal}/spaces/[slug]/notes`,
        NewNote: `${baseUrl.Portal}/spaces/[slug]/notes/new-note`,
        Index: `${baseUrl.Portal}/spaces/[slug]`,
        settings: {
          account: {
            Index: `${baseUrl.Portal}/spaces/[slug]/settings/account`,
            Profile: `${baseUrl.Portal}/spaces/[slug]/settings/account/profile`,
            Security: `${baseUrl.Portal}/spaces/[slug]/settings/account/security`
          },
          Index: `${baseUrl.Portal}/spaces/[slug]/settings`,
          space: {
            Billing: `${baseUrl.Portal}/spaces/[slug]/settings/space/billing`,
            General: `${baseUrl.Portal}/spaces/[slug]/settings/space/general`,
            Index: `${baseUrl.Portal}/spaces/[slug]/settings/space`,
            Members: `${baseUrl.Portal}/spaces/[slug]/settings/space/members`
          }
        }
      }
    }
  },
  marketing: {
    Api: `${baseUrl.Marketing}/api`,
    Blog: `${baseUrl.Marketing}/blog`,
    Careers: `${baseUrl.Marketing}/careers`,
    Contact: `${baseUrl.Marketing}/contact`,
    CookiePolicy: `${baseUrl.Marketing}/cookie-policy`,
    Docs: `${baseUrl.Marketing}/docs`,
    Index: `${baseUrl.Marketing}/`,
    Pricing: `${baseUrl.Marketing}/pricing`,
    PrivacyPolicy: `${baseUrl.Marketing}/privacy-policy`,
    Roadmap: 'https://achromatic.canny.io',
    Story: `${baseUrl.Marketing}/story`,
    TermsOfUse: `${baseUrl.Marketing}/terms-of-use`
  }
} as const;

type ExtractSlugRoutes<T> =
  T extends Record<string, unknown>
    ? {
        [K in keyof T]: T[K] extends string
          ? T[K] extends `${string}[slug]${string}`
            ? T[K]
            : never
          : ExtractSlugRoutes<T[K]>;
      }[keyof T]
    : never;

type SpacesSlugRoutes = ExtractSlugRoutes<typeof routes.portal.spaces.slug>;

export function replaceSpaceSlug(
  route: SpacesSlugRoutes,
  slug: string
): string {
  if (route.indexOf('[slug]') === -1) {
    throw new Error(
      `Invalid route: ${route}. Route must contain the placeholder [slug].`
    );
  }

  return route.replace('[slug]', slug);
}

export function getPathname(route: string, baseUrl: string): string {
  return new URL(route, baseUrl).pathname;
}

export function getSpaceLogoUrl(spaceId: string, hash: string): string {
  return `${routes.portal.Api}/space-logos/${spaceId}?v=${hash}`;
}

export function getUserImageUrl(userId: string, hash: string): string {
  return `${routes.portal.Api}/user-images/${userId}?v=${hash}`;
}

export function getContactImageUrl(contactId: string, hash: string): string {
  return `${routes.portal.Api}/contact-images/${contactId}?v=${hash}`;
}
